import { Avatar, Button, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  Record,
  RecordActive,
  SmallCheckmark,
  TrashCanBlackSmall,
  View,
} from 'Images';
import { useEffect, useRef, useState } from 'react';
import { Info } from './Info';
import { MAX_FILE_SIZE_IN_BYTES } from 'Config';

interface IScreenCapture {
  recordedBlob: Blob | null;
  setRecordedBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
}

export const ScreenCapture = ({
  recordedBlob,
  setRecordedBlob,
}: IScreenCapture) => {
  const [isRecording, setIsRecording] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showError, setShowError] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Feedback);
  const isDesktop = useIsDesktop();

  interface IDisplayMediaOptions extends DisplayMediaStreamOptions {
    preferCurrentTab?: boolean;
  }

  const startRecording = async () => {
    setShowError(false);
    try {
      const displayMediaOptions: IDisplayMediaOptions = {
        video: true,
        preferCurrentTab: true,
      };
      const stream = await navigator.mediaDevices.getDisplayMedia(
        displayMediaOptions
      );

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: 'video/webm',
        });
        setRecordedBlob(blob);
        recordedChunksRef.current = [];
      };

      mediaRecorder.start();
      setIsRecording(true);
      setShowPreview(false);
    } catch (err) {
      console.error('Error starting screen recording:', err);
      setShowError(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  useEffect(() => {
    if (recordedBlob && recordedBlob.size > MAX_FILE_SIZE_IN_BYTES) {
      setShowError(true);
      deleteRecording();
    }
  }, [recordedBlob]);

  const deleteRecording = () => {
    setRecordedBlob(null);
    setShowPreview(false);
  };

  return (
    <Stack
      sx={{
        width: '100%',
        maxWidth: '600px',
      }}
    >
      {recordedBlob ? (
        <Stack
          sx={{
            gap: '16px',
            mt: isDesktop ? undefined : '16px',
          }}
        >
          <Stack
            sx={{
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'primary.shade_200',
              gap: '8px',
              p: '12px 16px',
              flexDirection: 'row',
              alignItems: 'center',
              bgcolor: 'white',
            }}
          >
            <Avatar
              alt=''
              src={SmallCheckmark}
              sx={{ width: '32px', height: '32px' }}
            />
            <Typography variant='body1'>{dictionary.recordingDone}</Typography>
          </Stack>
        </Stack>
      ) : (
        <Button
          variant='secondary'
          sx={{
            p: '5px 10px 5px 20px',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderColor: showError ? '#E12D2D' : '#B7CCFF',
            color: 'text.primary',
            ':hover, :focus': {
              bgcolor: '#FFF',
              color: 'text.primary',
              borderColor: showError ? '#E12D2D' : '#B7CCFF',
            },
          }}
          onClick={isRecording ? stopRecording : startRecording}
        >
          <Typography variant='body3'>
            {isRecording ? dictionary.stopRecording : dictionary.startRecording}
          </Typography>
          <Avatar
            alt=''
            src={isRecording ? RecordActive : Record}
            sx={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              animation: isRecording
                ? 'pulse 1.5s infinite ease-in-out'
                : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.4)',
                },
                '70%': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
                },
              },
            }}
          />
        </Button>
      )}
      {!recordedBlob && <Info showError={showError} />}
      {recordedBlob && (
        <Stack
          direction={'row'}
          sx={{
            justifyContent: 'end',
            mt: '16px',
            gap: '22px',
            pr: '10px',
          }}
        >
          <Info showError={showError} />
          <Button
            sx={{
              alignSelf: 'flex-end',
              color: 'black',
              gap: '4px',
            }}
            onClick={deleteRecording}
          >
            <Avatar
              alt=''
              src={TrashCanBlackSmall}
              sx={{ width: '24px', height: '24px' }}
            />
            <Typography variant='body5'>{dictionary.delete}</Typography>
          </Button>
          <Button
            sx={{
              alignSelf: 'flex-end',
              color: 'black',
              gap: '4px',
            }}
            onClick={() => setShowPreview(true)}
          >
            <Avatar alt='' src={View} sx={{ width: '24px', height: '24px' }} />
            <Typography variant='body5'>{dictionary.view}</Typography>
          </Button>
        </Stack>
      )}
      {showPreview && recordedBlob && (
        <video
          controls
          src={URL.createObjectURL(recordedBlob)}
          style={{
            maxWidth: '800px',
            marginTop: '42px',
            padding: '16px',
            borderRadius: '10px',
            backgroundColor: 'white',
            border: '1px solid',
            borderColor: '#B7CCFF',
          }}
        />
      )}
    </Stack>
  );
};
